import type { ReactNode } from "react";
import { SlideHeader } from "../../../SlideHeader";
import { SupLink } from "../../../SlideContext";
import { ChevronStep } from "../../../ChevronStep";

// The whole pitch on one slide (TPF, 2026-09-01; acts merged into the era bar
// 2026-09-02). The story is three acts, stated once, big, as the era bar that
// sits directly on the timeline — each act a tall card with its numeral
// (10 months / €1.25M / 24 months), headline and one body line, spanning the
// milestone columns it covers.
//   Act 1 — ten months behind us: bootstrapped, self-funded, unpaid → a
//           shipped product impressing the industry, in use by major brands.
//   Act 2 — today: the raise. €1.25M funds the next 24 months of market
//           capture and brand establishment.
//   Act 3 — 24 months ahead: self-sustaining, highly profitable, growing
//           on revenue — no further raise.
// Timeline columns are equal-width rather than time-proportional so the
// bootstrap milestones don't crowd; the era bar carries the proportions in
// words instead.
const ERAS: Array<{
  label: string;
  figure: string;
  unit: string;
  headline: string;
  body: string;
  span: string;
  today?: boolean;
}> = [
  {
    label: "Past",
    figure: "10",
    unit: "months",
    headline: "Bootstrapped. Self-funded. Launched product.",
    body: "A shipped product that is impressing the industry and in active use by major brands.",
    span: "Dec '25 → Sep '26",
  },
  {
    label: "Present",
    figure: "€1.25M",
    unit: "raise",
    headline: "Capital for rapid mind & market capture in mobile app marketing.",
    body: "24 months of brand and performance marketing across self-serve and enterprise — own the category's mindshare, then its customers.",
    span: "Sep – Dec '26",
    today: true,
  },
  {
    label: "Future",
    figure: "24",
    unit: "months to:",
    headline: "Self-sustaining. Growing rapidly.",
    body: "By Dec '28: cash-flow positive at €3.7M ARR and growing 2.8× a year — growth funded from revenue, not further raises.",
    span: "→ Dec '28",
  },
];

// Information architecture (TPF, 2026-09-01): three labelled zones under the
// header, each holding one kind of fact —
//   1. The company    — what it is (classification), how it's shaped
//                       (structure), how big the market is (TAM)
//   2. The trajectory — the timeline, era bar cut into the three acts
//   3. Structural advantages — the three key selling points from the deck
const IDENTITY_FACTS: Array<{ facts: string[]; pill: string }> = [
  {
    facts: ["Agentic Software Factory"],
    pill: "bg-blue-50 text-blue-700 ring-blue-200",
  },
  {
    facts: ["EU-based, US-incorporated"],
    pill: "bg-slate-100 text-slate-700 ring-slate-300",
  },
];

function ZoneCaption({ children }: { children: ReactNode }) {
  return (
    <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.18em] text-blue-700">
      {children}
    </p>
  );
}

const MILESTONES: Array<{
  when: string;
  label: string;
  detail: string;
  now?: boolean;
  funded?: boolean;
}> = [
  {
    when: "Dec '25 – Feb '26",
    label: "Founded → private alpha",
    detail:
      "Two founders, self-funded. Proof of concept becomes a working platform.",
  },
  {
    when: "Apr '26",
    label: "Quiet public beta",
    detail: "Deliberately quiet — 10 design partners; proof before promotion.",
  },
  {
    when: "Jun – Aug '26",
    label: "Industry exposure",
    detail:
      "First paying customers, first logos, practitioners vouching — credibility earned, not bought.",
  },
  {
    when: "Sep – Dec '26",
    label: "Pre-seed close",
    detail: "€1.25M SAFE — amplify the proof: marketing scale-up begins.",
    now: true,
  },
  {
    when: "2027",
    label: "Market expansion",
    detail: "GTM-heavy plan; revenue covers the whole 24-month spend.",
    funded: true,
  },
  {
    when: "Early '28",
    label: "Revenue covers burn crossover point",
    detail: "Month 13 — ~€1.4M still in bank.",
    funded: true,
  },
  {
    when: "Dec '28",
    label: "€3.7M ARR, CF-positive",
    detail: "Growing 2.8× YoY — Series A optional.",
    funded: true,
  },
];

export default function ThePitch() {
  return (
    <div className="flex w-full flex-col px-6 pt-8 pb-4 text-slate-900 md:h-full md:px-24 md:pt-16 md:pb-2 md:[&_header]:mb-2.5">
      <SlideHeader tab eyebrow="One-Pager" />

      {/* Zone 1 — Background: where the company sits (the category ladder,
          moved here from slide 15 by TPF 2026-09-08, replacing the pills that
          carried the same three rungs), then structure. */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
        <div className="-mx-6 max-w-full overflow-x-auto px-6 md:mx-0 md:overflow-visible md:px-0">
        <div className="flex w-max items-stretch md:w-auto">
          <ChevronStep first size="sm">B2B SaaS</ChevronStep>
          <ChevronStep size="sm">MarTech</ChevronStep>
          <ChevronStep size="sm">Mobile Apps &amp; Games</ChevronStep>
          <ChevronStep size="sm">ASO</ChevronStep>
          <ChevronStep last terminal size="sm">
            Constant ASO Platform
          </ChevronStep>
        </div>
        </div>
        {IDENTITY_FACTS.map((group, gi) => (
          <div key={gi} className="contents">
            {group.facts.map((fact) => (
              <span
                key={fact}
                className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold ring-1 md:px-3 md:py-1 md:text-xs ${group.pill}`}
              >
                {fact}
              </span>
            ))}
          </div>
        ))}
      </div>

      {/* Zone 2 — the three acts, led by the H1 (no caption, TPF 2026-09-02) */}
      <div className="mt-4 md:mt-[17px]">
      <h1 className="mb-4 text-3xl font-semibold leading-tight tracking-tight md:mb-3 md:text-5xl md:leading-[1.08]">
        One raise takes Apptonomy from first customers to{" "}
        <span className="text-blue-700">€3.7M ARR.</span>
      </h1>
      {/* The three acts — decoupled from the milestone grid (TPF, 2026-09-02):
          a 2:3:2 strip so Today, the subject of the deck, takes the centre
          3/7 and lays out side-by-side. The timeline below keeps its seven
          equal columns; the now-dot (column 4) lands under this card's centre. */}
      <div className="grid grid-cols-1 gap-2 md:grid-cols-[2fr_3fr_2fr] md:gap-2">
        {ERAS.map((era, i) =>
          era.today ? (
            <div
              key={era.label}
              className="flex flex-col rounded-xl bg-blue-600 px-4 pt-1.5 pb-3 text-white md:px-5 md:pt-2 md:pb-3.5"
            >
              <div className="relative z-10 -ml-4 flex md:-ml-9">
                <ChevronStep
                  first={i === 0}
                  size="lg"
                  tone="bg-white text-blue-700"
                  stroke="bg-blue-800"
                >
                  <span className="flex flex-col leading-tight">
                    {era.label}
                    <span className="text-[11px] font-medium normal-case tracking-normal text-blue-600 md:text-xs">
                      {era.span}
                    </span>
                  </span>
                </ChevronStep>
              </div>
              <p className="mt-1 flex flex-wrap items-baseline gap-x-2">
                <span className="text-4xl font-bold tracking-tight md:text-5xl md:leading-none">
                  {era.figure}
                </span>
                <span className="text-base font-semibold text-blue-100 md:text-lg">
                  {era.unit}
                </span>
              </p>
              <p className="mt-1.5 text-base font-bold leading-tight tracking-tight md:text-lg">
                {era.headline}
              </p>
              <p className="mt-1 text-xs leading-snug text-blue-50 md:text-sm">
                {era.body}
              </p>
            </div>
          ) : (
            <div
              key={era.label}
              className="flex flex-col rounded-xl bg-slate-100 px-4 pt-1.5 pb-3 text-slate-900 ring-1 ring-inset ring-slate-200 md:px-5 md:pt-2 md:pb-3.5"
            >
              <div className={`relative z-10 flex ${i === 0 ? "-ml-4 md:-ml-5" : "-ml-4 md:-ml-9"}`}>
                <ChevronStep
                  first={i === 0}
                  size="lg"
                  tone={i === 0 ? "bg-slate-300 text-slate-800" : "bg-slate-800 text-white"}
                  stroke={i === 0 ? "bg-slate-500" : "bg-white"}
                >
                  <span className="flex flex-col leading-tight">
                    {era.label}
                    <span
                      className={`text-[11px] font-medium normal-case tracking-normal md:text-xs ${
                        i === 0 ? "text-slate-600" : "text-slate-300"
                      }`}
                    >
                      {era.span}
                    </span>
                  </span>
                </ChevronStep>
              </div>
              <p className="mt-1 flex flex-wrap items-baseline gap-x-2">
                <span className="text-4xl font-bold tracking-tight md:text-5xl md:leading-none">
                  {era.figure}
                </span>
                <span className="text-base font-semibold text-slate-500 md:text-lg">
                  {era.unit}
                </span>
              </p>
              <p className="mt-1.5 text-base font-bold leading-tight tracking-tight md:text-lg">
                {era.headline}
              </p>
              <p className="mt-1 text-xs leading-snug text-slate-700 md:text-sm">
                {era.body}
              </p>
            </div>
          )
        )}
      </div>

      </div>

      {/* Zone 3 — Timeline */}
      <div className="mt-4 md:mt-[17px]">
      <ZoneCaption>Timeline</ZoneCaption>
      <div className="relative md:rounded-lg md:bg-slate-50 md:px-2 md:pb-3 md:pt-4 md:ring-1 md:ring-slate-200">
        {/* the spine */}
        <div
          aria-hidden="true"
          className="absolute left-2 right-2 top-[46px] hidden h-px bg-slate-300 md:block"
        />
        <div className="flex flex-col gap-2 md:grid md:grid-cols-7 md:gap-2">
          {MILESTONES.map((m) => (
            <div key={m.when} className="relative flex items-start gap-2 md:flex-col md:items-stretch md:gap-0">
              {/* date label — above the dot, on the spine */}
              <p
                className={`shrink-0 whitespace-nowrap text-[10px] font-bold uppercase leading-4 tracking-[0.1em] md:mb-1 md:text-center md:text-[11px] ${
                  m.now ? "text-blue-700" : "text-slate-500"
                }`}
              >
                {m.when}
              </p>
              {/* dot on the spine */}
              <div className="hidden md:flex md:h-5 md:items-center md:justify-center">
                <span
                  className={`z-10 h-3 w-3 rounded-full ring-2 ring-white ${
                    m.now
                      ? "bg-blue-600 outline outline-2 outline-blue-300"
                      : m.funded
                        ? "bg-blue-500"
                        : "bg-slate-400"
                  }`}
                />
              </div>
              <div
                className={`flex-1 rounded-lg p-2 ring-1 md:mt-0.5 ${
                  m.now
                    ? "bg-blue-50 ring-2 ring-blue-400"
                    : "bg-white ring-slate-200"
                }`}
              >
                <p className="text-[13px] font-bold leading-tight tracking-tight text-slate-900 md:text-sm">
                  {m.label}
                </p>
                <p className="mt-1 text-[11px] leading-snug text-slate-600 md:text-xs">
                  {m.detail}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
      </div>

      {/* Zone 4 — Structural advantages: capital structure, cost structure, team */}
      <div className="mt-4 md:mt-[13px]">
      <ZoneCaption>Structural advantages</ZoneCaption>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3 md:gap-4">
        <div className="rounded-xl bg-white p-4 ring-1 ring-slate-200 md:p-3">
          <p className="text-sm font-bold tracking-tight text-slate-900 md:text-base">
            €1.25M — the only round we need
          </p>
          {/* Terms and allocation are facts, not arguments: they scan as a
              compact data row so the sentence below carries one idea alone. */}
          <p className="mt-1.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500 md:text-xs">
            SAFE · 12–15% dilution
            <span className="mx-1.5 text-slate-300">|</span>
            52% GTM · 32% founders
          </p>
          <p className="mt-1.5 text-xs leading-snug text-slate-700 md:text-[13px]">
            €1.25M goes further because AI runs across the whole business
            stack. Revenue covers the whole 24-month plan; the base case
            reaches cash-flow positive without a bridge — a Series A is
            optional.
            <SupLink to="use-of-funds">19</SupLink>
          </p>
        </div>
        <div className="rounded-xl bg-white p-4 ring-1 ring-slate-200 md:p-3">
          <p className="text-sm font-bold tracking-tight text-slate-900 md:text-base">
            We built an agentic software factory
            <SupLink to="ai-native-factory">E</SupLink>
          </p>
          <p className="mt-1.5 text-xs leading-snug text-slate-700 md:text-[13px]">
            Builds what we specify, monitors production, repairs itself, ships
            nightly — unattended. Engineering is
            ~17% of spend — one salary plus tooling — where competitors
            allocate 40%+. Every euro they
            spend rebuilding, we spend acquiring customers.
          </p>
        </div>
        <div className="rounded-xl bg-white p-4 ring-1 ring-slate-200 md:p-3">
          <p className="text-sm font-bold tracking-tight text-slate-900 md:text-base">
            A rare founder combination
          </p>
          <p className="mt-1.5 text-xs leading-snug text-slate-700 md:text-[13px]">
            A 12-year ASO practitioner who authored the methodology × a
            Netflix-rooted product CEO who engineered the factory — the pairing
            the moat rests on.
            <SupLink to="moat-tech-edge">11</SupLink>
          </p>
        </div>
      </div>
      </div>
    </div>
  );
}
